import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import { useCreateMerchantTerminalContractMutation } from "Api/Mutations/Merchants/useCreateMerchantTerminalContractMutation";
import { useUpdateMerchantTerminalContractMutation } from "Api/Mutations/Merchants/useUpdateMerchantTerminalContractMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { contractSchema } from "Components/Shared/Merchant/Products/TerminalContracts/schema";
import { TerminalContractTypeDropdown } from "Components/Shared/Merchant/Products/TerminalContracts/TerminalContractTypeDropdown";
import {
  type MerchantAppProductDto,
  type MerchantTerminalContractDto,
  MerchantTerminalContractType,
} from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import type { z } from "zod";

const COMPONENT_RESOURCES = Resources.Components.Merchant.TerminalContracts;

export type TerminalContractForm = z.infer<typeof contractSchema>;

type Props = {
  contract: Partial<MerchantTerminalContractDto>;
  merchantPublicID: string;
  onAfterSubmit?: (isSuccess: boolean) => void;
  merchantProducts: Array<MerchantAppProductDto>;
};

export const TerminalContractForm = ({
  onAfterSubmit,
  merchantPublicID,
  merchantProducts,
  contract: {
    publicID = "",
    type = MerchantTerminalContractType.UCB,
    ucbTerminalContractDetail,
    comgateTerminalContractDetail: initialComgateDetail,
  },
}: Props) => {
  const { t } = useResource();
  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = useForm<TerminalContractForm>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      publicID,
      type,
      merchantPublicID,
      ucbTerminalContractDetail: {
        gprsCount: ucbTerminalContractDetail?.gprsCount || 0,
      },
      comgateTerminalContractDetail: {
        currencies: initialComgateDetail?.currencies || "CZK",
        tariff: initialComgateDetail?.tariff || null,
      },
    },
  });

  const selectedType = watch("type");

  const { mutate: createContract, isPending: isCreatingContractPending } =
    useCreateMerchantTerminalContractMutation({
      merchantPublicID,
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.AddSuccess));
        onAfterSubmit?.(true);
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.AddError));
        onAfterSubmit?.(false);
      },
    });

  const { mutate: updateContract, isPending: isUpdatePending } =
    useUpdateMerchantTerminalContractMutation({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.UpdateSuccess));
        onAfterSubmit?.(true);
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.UpdateError));
        onAfterSubmit?.(false);
      },
    });

  const isPending = isCreatingContractPending || isUpdatePending;

  const onSubmit = (formData: TerminalContractForm) => {
    const { type, ucbTerminalContractDetail, comgateTerminalContractDetail } =
      formData;

    const payload = {
      publicID,
      merchantPublicID,
      type,
      ucbTerminalContractDetail:
        type === MerchantTerminalContractType.UCB
          ? ucbTerminalContractDetail
          : null,
      comgateTerminalContractDetail:
        type === MerchantTerminalContractType.COMGATE &&
        comgateTerminalContractDetail
          ? {
              ...comgateTerminalContractDetail,
              // Terminal counts are derived from the facilities on the server; the form only echoes them.
              terminalCount: initialComgateDetail?.terminalCount ?? 0,
              softPosCount: initialComgateDetail?.softPosCount ?? 0,
              // Pass through read-only status fields preserved from the original contract
              contractAccepted: initialComgateDetail?.contractAccepted ?? false,
              handoverConfirmed:
                initialComgateDetail?.handoverConfirmed ?? false,
            }
          : null,
    };

    if (publicID) {
      updateContract({
        request: payload,
        merchantPublicID,
        terminalContractPublicID: publicID,
      });
    } else {
      createContract(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box>
        <TerminalContractTypeDropdown
          control={control}
          name="type"
          merchantProducts={merchantProducts}
        />
        {selectedType === MerchantTerminalContractType.UCB && (
          <FormInput
            control={control}
            name="ucbTerminalContractDetail.gprsCount"
            label={t(COMPONENT_RESOURCES.Form.Fields.GprsCount)}
            type="number"
            min={0}
          />
        )}
        {selectedType === MerchantTerminalContractType.COMGATE && (
          <>
            <FormInput
              control={control}
              name="comgateTerminalContractDetail.currencies"
              label={t(COMPONENT_RESOURCES.Form.Fields.Currencies)}
            />
            <FormInput
              control={control}
              name="comgateTerminalContractDetail.tariff"
              label={t(COMPONENT_RESOURCES.Form.Fields.Tariff)}
            />
            {!!publicID && (
              <Box mt={1}>
                <Typography variant="body2">
                  {t(COMPONENT_RESOURCES.Form.Fields.TerminalCount)}:{" "}
                  {initialComgateDetail?.terminalCount ?? 0},{" "}
                  {t(COMPONENT_RESOURCES.Form.Fields.SoftPosCount)}:{" "}
                  {initialComgateDetail?.softPosCount ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(COMPONENT_RESOURCES.Form.Fields.CountsFromFacilitiesHint)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
      <PrimaryButton
        fullWidth
        type="submit"
        isLoading={isPending}
        variant="contained"
        color="primary"
        disabled={!isDirty}
      >
        {t(Resources.Common.Save)}
      </PrimaryButton>
    </form>
  );
};
